import { motion } from 'motion/react';
import { googleReviews, brandInfo } from '@/mocks/homeData';

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <span key={star} className="w-4 h-4 flex items-center justify-center">
          <i className={`${star <= rating ? 'ri-star-fill text-amber-500' : 'ri-star-line text-background-300'} text-sm`}></i>
        </span>
      ))}
    </div>
  );
}

const avatarColors = [
  'bg-primary-500',
  'bg-accent-500',
  'bg-secondary-500',
  'bg-primary-600',
  'bg-accent-600',
  'bg-secondary-600',
];

export default function GoogleReviewsSection() {
  const { rating } = brandInfo;

  return (
    <section className="py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="inline-block bg-primary-100 text-primary-900 px-4 py-1.5 rounded-full text-xs md:text-sm font-semibold mb-4">
            Google Reviews
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4">
            What Customers Say
          </h2>

          <div className="flex items-center justify-center gap-3 mt-4 flex-wrap">
            <div className="flex items-center bg-background-50 border border-background-200/70 rounded-full px-5 py-2.5 gap-3">
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/5/53/Google_%22G%22_Logo.svg"
                alt="Google"
                className="w-6 h-6 shrink-0"
              />
              <div className="flex items-center gap-2">
                <span className="font-bold text-foreground-950 text-lg">{rating.score}</span>
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <span key={s} className="w-4 h-4 flex items-center justify-center">
                      <i className={`${s <= Math.round(rating.score) ? 'ri-star-fill text-amber-500' : 'ri-star-line text-background-300'} text-sm`}></i>
                    </span>
                  ))}
                </div>
              </div>
              <span className="text-foreground-500 text-sm">({rating.count} reviews)</span>
            </div>
          </div>

          <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto mt-4">
            Real feedback from verified customers who visited our store in Yallapura, Tumakuru.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {googleReviews.map((review, index) => (
            <motion.div
              key={review.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, delay: index * 0.1, ease: 'easeOut' }}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              className="bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6 hover:border-primary-300 transition-all duration-300 cursor-default"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-background-50 font-semibold text-sm shrink-0 ${avatarColors[index % avatarColors.length]}`}>
                  {review.initials}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-foreground-950 text-sm truncate">{review.name}</p>
                    {review.verified && (
                      <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0">
                        <i className="ri-check-line"></i>
                        Verified
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <StarRating rating={review.rating} />
                    <span className="text-foreground-400 text-xs">{review.date}</span>
                  </div>
                </div>
              </div>

              <p className="text-foreground-700 text-sm leading-relaxed mb-3">
                &ldquo;{review.text}&rdquo;
              </p>

              <div className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-shopping-bag-3-line text-foreground-400 text-xs"></i>
                </span>
                <span className="text-foreground-400 text-xs">{review.service}</span>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center mt-10 flex flex-col sm:flex-row items-center justify-center gap-3"
        >
          <a
            href={brandInfo.googleWriteReviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-pencil-line text-xs"></i>
            </span>
            Write a Review
          </a>

          <a
            href={brandInfo.googleReviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-background-50 border border-background-200/70 hover:border-primary-300 text-foreground-700 hover:text-primary-600 px-5 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
          >
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/5/53/Google_%22G%22_Logo.svg"
              alt="Google"
              className="w-4 h-4"
            />
            View all {rating.count} reviews on Google
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-external-link-line text-xs"></i>
            </span>
          </a>
        </motion.div>
      </div>
    </section>
  );
}